import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Segmented,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  FilePdfOutlined,
  PrinterOutlined,
  RightOutlined,
  DownOutlined,
  ReloadOutlined,
  SettingOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileSearchOutlined,
  RollbackOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import invoiceService from '../../../services/invoiceService';
import userService from '../../../services/userService';
import config from '../../../config';
import xeroIntegrationService from '../../../services/xeroIntegrationService';
import paymentService from '../../../services/paymentService';
import ClientOptionLabel, { buildClientSearchText, getClientSelectedLabel } from '../../common/ClientOptionLabel';
import { getInvoiceBuilderPagination } from './invoiceBuilderPagination';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const formatCurrency = (value) =>
  value || value === 0 ? `£${Number(value).toFixed(2)}` : '—';

const isAdminUser = (user) => {
  if (!user) return false;
  const roles = user.roles || [];
  if (roles.some((role) => ['admin', 'superadmin'].includes(String(role.name).toLowerCase()))) {
    return true;
  }
  if (['admin', 'superadmin'].includes(String(user.role || '').toLowerCase())) {
    return true;
  }
  return user.is_admin === 1 || user.is_admin === true;
};

const DEFAULT_SETTINGS = {
  organization_name: config.APP_NAME,
  organization_address: '',
  organization_phone: config.SUPPORT_PHONE,
  primary_color: '#0F172A',
  accent_color: '#C29946',
  footer_text: 'Thank you for your business.',
  vat_rate: 20,
  invoice_number_prefix: 'INV-',
  invoice_number_padding: 5,
  invoice_number_next: 1,
  invoice_number_format: '{PREFIX}{SEQ}',
};

const orderItemColumns = [
  {
    title: 'Item',
    dataIndex: 'order_products_name',
    key: 'item',
    render: (text) => text || '—',
  },
  {
    title: 'Quantity',
    dataIndex: 'order_products_qty',
    key: 'qty',
    width: 120,
    align: 'center',
  },
  {
    title: 'Price',
    dataIndex: 'order_products_price',
    key: 'price',
    width: 140,
    align: 'right',
    render: (price) => formatCurrency(price),
  },
  {
    title: 'Total',
  key: 'total',
  width: 140,
  align: 'right',
  render: (_, item) => {
    const price = parseFloat(item.order_products_price || 0);
      const qty = parseFloat(item.order_products_qty || 0);
      return formatCurrency(price * qty);
    },
  },
];

// Same style/shape as Sales Report -> Order Details (but without some columns)
const getOrderColumns = () => [
  { title: 'Order #', dataIndex: 'id', width: 110 },
  {
    title: 'Date',
    dataIndex: 'property_date',
    width: 130,
    render: (value) => (value ? moment(value).format('YYYY-MM-DD') : '—'),
  },
  {
    title: 'Client',
    dataIndex: 'client_name',
    width: 180,
    ellipsis: true,
    render: (value, row) => value || row.customer_name || row.user?.name || '—',
  },
  {
    title: 'Property',
    key: 'property',
    width: 180,
    render: (_, row) => row.property_name || row.property_unit || row.property_address || '—',
  },
  {
    title: 'Address',
    key: 'address',
    ellipsis: true,
    render: (_, row) => row.property_address || row.delivery_address_1 || '—',
  },
  {
    title: 'Post Code',
    dataIndex: 'postcode',
    width: 120,
    render: (v) => v || '—',
  },
  {
    title: 'Total',
    dataIndex: 'order_total',
    width: 120,
    align: 'right',
    render: formatCurrency,
  },
  {
    title: 'Invoice',
    dataIndex: 'invoice_id',
    width: 140,
    render: (inv, row) => (inv ? row.invoice_number || `#${inv}` : '—'),
  },
];

export default function InvoicesPage() {
  const [settingsForm] = Form.useForm();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [logoFile, setLogoFile] = useState();
  const [customers, setCustomers] = useState([]);
  const [clientId, setClientId] = useState();
  const [eligibleClientIds, setEligibleClientIds] = useState(null);
  const [eligibleClientsLoading, setEligibleClientsLoading] = useState(false);
  const [range, setRange] = useState([null, null]);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, invoice: null });
  const [editSelection, setEditSelection] = useState([]);
  const [editOrders, setEditOrders] = useState([]);
  const [editReference, setEditReference] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('builder');
  const [ordersViewMode, setOrdersViewMode] = useState('default'); // default | items
  const [showAllOrders, setShowAllOrders] = useState(false);
  const [ordersPageSize, setOrdersPageSize] = useState(20);
  const [ordersPage, setOrdersPage] = useState(1);
  const [showAllInvoices, setShowAllInvoices] = useState(false);
  const [invoicesPageSize, setInvoicesPageSize] = useState(20);
  const [invoicesPage, setInvoicesPage] = useState(1);
  const [xeroStatus, setXeroStatus] = useState(null);
  const [xeroLoading, setXeroLoading] = useState(false);

  const [createReferenceModalOpen, setCreateReferenceModalOpen] = useState(false);
  const [createReference, setCreateReference] = useState('');
  const [createInvoiceSubmitting, setCreateInvoiceSubmitting] = useState(false);
  const [xeroAccounts, setXeroAccounts] = useState([]);
  const [createXeroAccountId, setCreateXeroAccountId] = useState();
  const [xeroAccountsLoading, setXeroAccountsLoading] = useState(false);
  const [capturingInvoiceId, setCapturingInvoiceId] = useState(null);

  const [previewModal, setPreviewModal] = useState({ open: false, invoice: null });
  const [previewMode, setPreviewMode] = useState('list'); // list | pdf
  const [previewOrdersView, setPreviewOrdersView] = useState('table'); // table | detailed
  const [previewTableMode, setPreviewTableMode] = useState('default'); // default | items
  const [pdfVariant, setPdfVariant] = useState('withItems'); // withItems | summary
  const [pdfUrls, setPdfUrls] = useState({});
  const [pdfLoading, setPdfLoading] = useState(false);
  const pdfIframeRef = useRef(null);
  const ordersRequestRef = useRef(0);
  const invoicesRequestRef = useRef(0);

  const [extraLine, setExtraLine] = useState({
    description: '',
    qty: null,
    unit_price: null,
  });

  const orderColumns = useMemo(() => getOrderColumns(), []);
  const previewColumns = useMemo(
    () => orderColumns.filter((c) => c.title !== 'Invoice'),
    [orderColumns]
  );
  const selectableOrderIds = useMemo(
    () => orders.filter((order) => !order.invoice_id).map((order) => order.id),
    [orders]
  );
  const visibleCustomers = useMemo(() => {
    // Once a period is selected, only show clients that have an invoiceable or
    // already-invoiced order in that exact period.
    if (!eligibleClientIds) return customers || [];
    return (customers || []).filter((customer) => eligibleClientIds.has(Number(customer.id)));
  }, [customers, eligibleClientIds]);

  const editInvoiceId = editModal?.invoice?.id;
  const editSelectableOrderIds = useMemo(() => {
    return (editOrders || [])
      .filter((order) => !order.invoice_id || order.invoice_id === editInvoiceId)
      .map((order) => order.id);
  }, [editOrders, editInvoiceId]);

  const expandedOrderItems = (record) => {
    const orderItems = record.order_products || [];
    if (!orderItems.length) {
      return <p className="text-gray-500 italic p-4">No item details available</p>;
    }

    return (
      <div className="p-4 bg-gray-50 rounded-md">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-medium">Order Items</h4>
          <div className="text-sm text-gray-500">Items: {orderItems.length}</div>
        </div>
        <Table
          columns={orderItemColumns}
          dataSource={orderItems}
          rowKey={(item) => `item-${item.order_products_id || Math.random()}`}
          pagination={false}
          size="small"
        />
      </div>
    );
  };

  const rangePayload = useMemo(() => {
    const [start, end] = range;
    return {
      date_from: start ? start.startOf('day').toISOString() : undefined,
      date_to: end ? end.endOf('day').toISOString() : undefined,
    };
  }, [range]);

  useEffect(() => {
    userService
      .getAllCustomers({ per_page: 500 })
      .then((data) => {
        const rows = data?.data || data?.users || data || [];
        setCustomers(rows.filter((user) => !isAdminUser(user)));
      })
      .catch(() => message.error('Failed to load clients'));
  }, []);

  useEffect(() => {
    loadInvoices();
    loadOrders();
    setSelectedOrders([]);
  }, [clientId, rangePayload.date_from, rangePayload.date_to]);

  useEffect(() => {
    let cancelled = false;

    if (!rangePayload.date_from && !rangePayload.date_to) {
      setEligibleClientIds(null);
      setEligibleClientsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setEligibleClientsLoading(true);
    invoiceService
      .listInvoiceableOrders({
        date_from: rangePayload.date_from,
        date_to: rangePayload.date_to,
        clients_only: 1,
      })
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data || res || [];
        setEligibleClientIds(
          new Set(
            rows
              .map((row) => Number(row?.client_id || row))
              .filter((clientId) => Number.isFinite(clientId) && clientId > 0)
          )
        );
      })
      .catch(() => {
        if (!cancelled) message.error('Failed to load clients for selected date');
      })
      .finally(() => {
        if (!cancelled) setEligibleClientsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [rangePayload.date_from, rangePayload.date_to]);

  useEffect(() => {
    if (!clientId || !customers.length) return;
    const clientStillExists = customers.some(
      (customer) => Number(customer.id) === Number(clientId)
    );
    if (!clientStillExists) {
      setClientId(undefined);
    }
  }, [clientId, customers]);

  useEffect(() => {
    setSelectedOrders((prev) => prev.filter((id) => selectableOrderIds.includes(id)));
  }, [selectableOrderIds]);

  useEffect(() => {
    // When editOrders reload, drop selections for orders no longer present
    const ids = new Set((editOrders || []).map((o) => o.id));
    setEditSelection((prev) => prev.filter((id) => ids.has(id)));
  }, [editOrders]);

  useEffect(() => {
    setXeroStatus(null);
    if (!clientId) return;
    setXeroLoading(true);
    xeroIntegrationService
      .getStatus(clientId)
      .then((res) => setXeroStatus(res || null))
      .catch(() => setXeroStatus(null))
      .finally(() => setXeroLoading(false));
  }, [clientId]);

  useEffect(() => {
    let cancelled = false;
    setXeroAccountsLoading(true);
    xeroIntegrationService
      .listAccounts()
      .then((response) => {
        if (!cancelled) setXeroAccounts(response?.data || []);
      })
      .catch(() => {
        if (!cancelled) setXeroAccounts([]);
      })
      .finally(() => {
        if (!cancelled) setXeroAccountsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    // Clean up blob URL on unmount / when changed
    return () => {
      Object.values(pdfUrls || {}).forEach((url) => {
        try {
          window.URL.revokeObjectURL(url);
        } catch (e) {
          // ignore
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (previewMode === 'pdf' && previewModal.invoice) {
      ensurePdfLoaded(previewModal.invoice.id, pdfVariant);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfVariant, previewMode, previewModal.invoice]);

  const loadOrders = async () => {
    const requestId = ++ordersRequestRef.current;
    setOrdersLoading(true);
    try {
      const res = await invoiceService.listAllInvoiceableOrders({
        client_id: clientId,
        date_from: rangePayload.date_from,
        date_to: rangePayload.date_to,
      });
      if (requestId === ordersRequestRef.current) {
        setOrders(res || []);
      }
    } finally {
      if (requestId === ordersRequestRef.current) {
        setOrdersLoading(false);
      }
    }
  };

  const loadInvoices = async () => {
    const requestId = ++invoicesRequestRef.current;
    setInvoiceLoading(true);
    try {
      const res = await invoiceService.listInvoices({
        client_id: clientId,
        date_from: rangePayload.date_from,
        date_to: rangePayload.date_to,
      });
      if (requestId === invoicesRequestRef.current) {
        setInvoices(res?.data || res || []);
      }
    } finally {
      if (requestId === invoicesRequestRef.current) {
        setInvoiceLoading(false);
      }
    }
  };

  const getPdfObjectUrl = (blobLike) => {
    const blob =
      blobLike instanceof Blob ? blobLike : new Blob([blobLike], { type: 'application/pdf' });
    return window.URL.createObjectURL(blob);
  };

  const handlePdf = async (invoiceId, { includeItems = true } = {}) => {
    try {
      const blob = await invoiceService.fetchInvoicePdf(invoiceId, { include_items: includeItems });
      const url = getPdfObjectUrl(blob);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) message.warning('Please allow popups to view the PDF');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      message.error('Failed to generate invoice PDF');
    }
  };

  const handlePrint = async (invoiceId, { includeItems = true } = {}) => {
    try {
      const blob = await invoiceService.fetchInvoicePdf(invoiceId, { include_items: includeItems });
      const url = getPdfObjectUrl(blob);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        message.warning('Please allow popups to print the PDF');
        return;
      }
      setTimeout(() => {
        try {
          win.focus();
          win.print();
        } catch (e) {
          // ignore
        }
      }, 800);
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      message.error('Failed to print invoice PDF');
    }
  };

  const loadSettings = async () => {
    try {
      const res = await invoiceService.getSettings();
      const merged = { ...DEFAULT_SETTINGS, ...(res || {}) };
      setSettings(merged);
      settingsForm.setFieldsValue(merged);
    } catch (e) {
      // ignore
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSettingsSaving(true);
      const values = await settingsForm.validateFields();
      const formData = new FormData();
      Object.entries(values).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== '') {
          formData.append(key, val);
        }
      });
      if (logoFile) {
        formData.append('logo', logoFile);
      }
      const res = await invoiceService.updateSettings(formData);
      const merged = { ...DEFAULT_SETTINGS, ...(res || {}) };
      setSettings(merged);
      setLogoFile(undefined);
      message.success('Invoice appearance updated');
      setSettingsOpen(false);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!selectedOrders.length) {
      message.error('Select at least one order');
      return;
    }
    setCreateReference('');
    const availableAccounts = xeroAccounts.filter(
      account => account.status === 'connected' && account.is_active
    );
    const selectedCustomer = customers.find(customer => Number(customer.id) === Number(clientId));
    const preferredAccount = availableAccounts.find(
      account => Number(account.id) === Number(selectedCustomer?.xero_account_id)
    ) || availableAccounts.find(account => account.is_default) || availableAccounts[0];
    setCreateXeroAccountId(preferredAccount?.id);
    setCreateReferenceModalOpen(true);
  };

  const submitCreateInvoice = async () => {
    if (!selectedOrders.length) {
      message.error('Select at least one order');
      return;
    }
    if (!createReference.trim()) {
      message.error('Please enter a reference');
      return;
    }
    const availableAccounts = xeroAccounts.filter(
      account => account.status === 'connected' && account.is_active
    );
    if (availableAccounts.length > 0 && !createXeroAccountId) {
      message.error('Select the Xero account for this invoice');
      return;
    }
    setCreateInvoiceSubmitting(true);
    try {
      const res = await invoiceService.createInvoice({
        client_id: clientId,
        order_ids: selectedOrders,
        reference: createReference.trim(),
        period_from: rangePayload.date_from,
        period_to: rangePayload.date_to,
        xero_account_id: createXeroAccountId || null,
      });
      if (res?.xero_last_error) {
        message.warning(`Invoice ${res?.number || res?.id} created, but Xero sync failed. Retry it from the Invoices tab.`);
      } else {
        message.success(`Invoice ${res?.number || res?.id} created`);
      }
      setCreateReferenceModalOpen(false);
      setSelectedOrders([]);
      await Promise.all([loadInvoices(), loadOrders()]);
    } catch (e) {
      message.error(e?.message || 'Failed to create invoice');
    } finally {
      setCreateInvoiceSubmitting(false);
    }
  };

  const handleCaptureInvoice = (invoice) => {
    const unpaidOrders = (invoice.orders || []).filter(
      order => Number(order.payment_status ?? 0) === 0,
    );
    if (!unpaidOrders.length) {
      message.info('All orders on this invoice are already paid');
      return;
    }

    Modal.confirm({
      title: 'Capture invoice payment from saved card?',
      content: `This will charge ${unpaidOrders.length} unpaid order${unpaidOrders.length === 1 ? '' : 's'} on invoice ${invoice.number || `#${invoice.id}`} using ${invoice.stripe_account_label || 'the saved Stripe card'}.`,
      okText: 'Capture payment',
      onOk: async () => {
        setCapturingInvoiceId(invoice.id);
        try {
          for (const order of unpaidOrders) {
            await paymentService.captureSavedCard(order.order_id);
          }
          await invoiceService.markInvoicePaid(invoice.id);
          message.success('Saved card charged and invoice marked paid');
        } catch (error) {
          message.error(error?.message || 'Unable to capture invoice payment');
          throw error;
        } finally {
          setCapturingInvoiceId(null);
          loadInvoices();
          loadOrders();
        }
      },
    });
  };

  const canSyncToXero = (invoice) => Boolean(
    invoice?.xero_account_id
      ? xeroAccounts.some(account => (
          Number(account.id) === Number(invoice.xero_account_id)
          && account.status === 'connected'
          && account.is_active
        ))
      : clientId && xeroStatus?.status === 'connected'
  );

  const handleSyncToXero = async (invoice) => {
    if (!canSyncToXero(invoice)) {
      message.warning('The Xero account for this invoice is not connected');
      return;
    }
    try {
      await xeroIntegrationService.syncInvoiceToXero(invoice.id);
      message.success('Queued Xero sync');
      loadInvoices();
    } catch (e) {
      message.error(e?.message || 'Failed to queue Xero sync');
    }
  };

  const getXeroViewUrl = (xeroInvoiceId) => {
    if (!xeroInvoiceId) return null;
    // Best-effort: open Xero invoice view by InvoiceID if supported by account.
    return `https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${encodeURIComponent(
      xeroInvoiceId
    )}`;
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await invoiceService.markInvoicePaid(invoiceId);
      message.success('Invoice marked as paid and orders updated');
      loadInvoices();
      loadOrders();
    } catch (e) {
      message.error(e?.message || 'Failed to mark paid');
    }
  };

  const handleRevertPaid = async (invoiceId) => {
    try {
      await invoiceService.revertInvoicePaid(invoiceId);
      message.success('Invoice reverted to draft and orders marked unpaid');
      loadInvoices();
      loadOrders();
    } catch (e) {
      message.error(e?.message || 'Failed to revert paid invoice');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    Modal.confirm({
      title: 'Delete invoice?',
      content: 'This will detach orders and delete the invoice. If it is synced to Xero, it will be voided in Xero first.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await invoiceService.deleteInvoice(invoiceId);
          message.success('Invoice deleted');
          loadInvoices();
          loadOrders();
        } catch (e) {
          message.error(e?.message || 'Failed to delete invoice');
        }
      },
    });
  };

  const openEditModal = async (invoice) => {
    setEditModal({ open: true, invoice });
    setEditSelection((invoice?.orders || []).map((o) => o.order_id));
    setEditReference(invoice?.reference || '');
    setExtraLine({
      description: invoice?.extra_line_description || '',
      qty: invoice?.extra_line_qty ?? null,
      unit_price: invoice?.extra_line_unit_price ?? null,
    });
    try {
      const res = await invoiceService.listAllInvoiceableOrders({
        client_id: invoice.customer_id,
        include_invoiced: true,
        invoice_id: invoice.id,
      });
      setEditOrders(res || []);
    } catch (e) {
      message.error('Failed to load orders for editing');
    }
  };

  const handleUpdateInvoice = async () => {
    if (!editModal.invoice) return;
    try {
      await invoiceService.updateInvoice(editModal.invoice.id, {
        order_ids: editSelection,
        reference: editReference?.trim() ? editReference.trim() : null,
        period_from: rangePayload.date_from,
        period_to: rangePayload.date_to,
        extra_line_description: extraLine.description?.trim() ? extraLine.description.trim() : null,
        extra_line_qty:
          extraLine.description?.trim() && extraLine.qty !== null ? Number(extraLine.qty) : null,
        extra_line_unit_price:
          extraLine.description?.trim() && extraLine.unit_price !== null
            ? Number(extraLine.unit_price)
            : null,
      });
      message.success('Invoice updated');
      setEditModal({ open: false, invoice: null });
      setEditOrders([]);
      loadInvoices();
      loadOrders();
    } catch (e) {
      message.error(e?.message || 'Failed to update invoice');
    }
  };

  const openPreviewModal = async (invoice) => {
    setPreviewModal({ open: true, invoice });
    setPreviewMode('list');
    setPreviewOrdersView('table');
    setPreviewTableMode('default');
    setPdfVariant('withItems');
    // reset pdf
    Object.values(pdfUrls || {}).forEach((url) => {
      try {
        window.URL.revokeObjectURL(url);
      } catch (e) {
        // ignore
      }
    });
    setPdfUrls({});
  };

  const ensurePdfLoaded = async (invoiceId, variant = pdfVariant) => {
    if (!invoiceId) return;
    if (pdfUrls[variant]) return;
    setPdfLoading(true);
    try {
      const blob = await invoiceService.fetchInvoicePdf(invoiceId, {
        include_items: variant === 'withItems',
      });
      const url = getPdfObjectUrl(blob);
      setPdfUrls((prev) => ({ ...prev, [variant]: url }));
    } catch (e) {
      message.error('Failed to load invoice PDF preview');
    } finally {
      setPdfLoading(false);
    }
  };

  const previewOrdersRows = useMemo(() => {
    const inv = previewModal.invoice;
    if (!inv) return [];
    const rows = (inv.orders || []).map((o) => ({
      id: o.order_id,
      property_date: o.property_date,
      property_name: o.property_name,
      property_address: o.property_address,
      postcode: o.postcode,
      order_total: o.amount ?? o.order_total,
      order_status: o.order_status,
      payment_status: o.payment_status,
      // invoices index payload includes nested items now
      order_products: o.order_products || [],
      client_name: inv.customer_name,
    }));
    return rows;
  }, [previewModal.invoice]);

  const previewSubtotal = useMemo(() => {
    return previewOrdersRows.reduce((sum, r) => sum + Number(r.order_total || 0), 0);
  }, [previewOrdersRows]);

  const previewExtraTotal = useMemo(() => {
    const inv = previewModal.invoice;
    if (!inv?.extra_line_description || inv.extra_line_qty == null || inv.extra_line_unit_price == null) return 0;
    return Number(inv.extra_line_qty) * Number(inv.extra_line_unit_price);
  }, [previewModal.invoice]);

  const invoiceStats = useMemo(() => {
    const paidInvoices = invoices.filter((inv) => inv.status === 'paid');
    const outstandingInvoices = invoices.filter(
      (inv) => inv.status !== 'paid' && inv.status !== 'cancelled'
    );
    const readyOrders = orders.filter((o) => !o.invoice_id);

    const sumInvoices = (arr) => arr.reduce((sum, inv) => sum + Number(inv.total || 0), 0);
    const sumOrders = (arr) => arr.reduce((sum, order) => sum + Number(order.order_total || 0), 0);

    return {
      paidCount: paidInvoices.length,
      paidTotal: sumInvoices(paidInvoices),
      outstandingCount: outstandingInvoices.length,
      outstandingTotal: sumInvoices(outstandingInvoices),
      readyCount: readyOrders.length,
      readyTotal: sumOrders(readyOrders),
    };
  }, [invoices, orders]);

  const renderPreviewOrderCards = () => {
    if (!previewOrdersRows.length) {
      return (
        <Card className="mb-3">
          <Text type="secondary">No orders attached to this invoice yet.</Text>
        </Card>
      );
    }

    return (
      <div className="space-y-3">
        {previewOrdersRows.map((order) => (
          <Card
            key={`order-card-${order.id}`}
            className="shadow-sm border border-gray-100"
            bodyStyle={{ padding: '16px 16px 8px' }}
          >
            <div className="flex flex-wrap justify-between gap-3 mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <Text type="secondary">Order</Text>
                  <Text strong>#{order.id}</Text>
                  <Tag color="blue">{order.order_status || '—'}</Tag>
                  <Tag color={order.payment_status ? 'green' : 'orange'}>
                    {order.payment_status ? 'Paid' : 'Unpaid'}
                  </Tag>
                </div>
                <div className="text-gray-500 text-sm">
                  {order.property_date ? moment(order.property_date).format('YYYY-MM-DD') : '—'} •{' '}
                  {order.property_name || order.property_unit || order.property_address || 'No property info'}
                </div>
              </div>
              <Space direction="vertical" align="end" size={2}>
                <Text type="secondary">Order total</Text>
                <Text strong style={{ fontSize: 18 }}>
                  {formatCurrency(order.order_total)}
                </Text>
                <Text type="secondary" className="text-xs">
                  {order.order_products?.length ? `${order.order_products.length} items` : 'No items'}
                </Text>
              </Space>
            </div>
            <Divider style={{ margin: '10px 0' }} />
            {order.order_products?.length ? (
              <Table
                columns={orderItemColumns}
                dataSource={order.order_products}
                pagination={false}
                size="small"
                rowKey={(item) => `order-${order.id}-item-${item.order_products_id || Math.random()}`}
              />
            ) : (
              <Text type="secondary">No item lines for this order.</Text>
            )}
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Invoices
          </Title>
          <Text type="secondary">Create, preview and manage invoices. Configure the invoice PDF from the settings gear.</Text>
        </div>
        <Space>
          <Tooltip title="Invoice settings">
            <Button icon={<SettingOutlined />} onClick={() => setSettingsOpen(true)}>
              Settings
            </Button>
          </Tooltip>
          <Button icon={<ReloadOutlined />} onClick={() => { loadOrders(); loadInvoices(); }}>
            Refresh
          </Button>
        </Space>
      </div>

      <Row gutter={[12, 12]} className="mb-4">
        {[
          {
            key: 'paid',
            title: 'Paid invoices',
            subtitle: formatCurrency(invoiceStats.paidTotal),
            value: `${invoiceStats.paidCount} paid`,
            icon: <CheckCircleOutlined className="text-green-600" />,
            bg: 'linear-gradient(135deg, #ecfdf3 0%, #d1fae5 100%)',
          },
          {
            key: 'outstanding',
            title: 'Unpaid / draft',
            subtitle: formatCurrency(invoiceStats.outstandingTotal),
            value: `${invoiceStats.outstandingCount} open`,
            icon: <ClockCircleOutlined className="text-amber-600" />,
            bg: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
          },
          {
            key: 'ready',
            title: 'Uninvoiced orders',
            subtitle: formatCurrency(invoiceStats.readyTotal),
            value: `${invoiceStats.readyCount} ready`,
            icon: <FileSearchOutlined className="text-blue-600" />,
            bg: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          },
        ].map((card) => (
          <Col xs={24} md={8} key={card.key}>
            <Card
              size="small"
              className="shadow-sm"
              bodyStyle={{ background: card.bg, borderRadius: 10, minHeight: 100 }}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {card.icon}
                    <Text type="secondary">{card.title}</Text>
                  </div>
                  <Title level={4} style={{ margin: 0 }}>
                    {card.value}
                  </Title>
                  <Text type="secondary">{card.subtitle}</Text>
                </div>
                <BarChartOutlined className="text-gray-400" />
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="mb-4">
        <Row gutter={12} align="middle">
          <Col xs={24} md={10}>
            <Select
              allowClear
              placeholder="Filter by client"
              showSearch
              loading={eligibleClientsLoading}
              optionFilterProp="search"
              style={{ width: '100%' }}
              optionRender={(option) => <ClientOptionLabel client={option?.data?.client || option?.data} />}
              options={(visibleCustomers || []).map((c) => ({
                label: getClientSelectedLabel(c),
                value: c.id,
                client: c,
                search: buildClientSearchText(c),
              }))}
              value={clientId}
              onChange={(val) => setClientId(val)}
            />
          </Col>
          <Col xs={24} md={10}>
            <RangePicker style={{ width: '100%' }} value={range} onChange={(val) => setRange(val || [null, null])} />
          </Col>
          <Col xs={24} md={4} style={{ textAlign: 'right' }}>
            <Button
              onClick={() => {
                setClientId(undefined);
                setRange([null, null]);
              }}
            >
              Clear
            </Button>
          </Col>
        </Row>
      </Card>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'builder',
            label: 'Invoice builder',
            children: (
              <Card
                title="Orders ready for invoicing"
                loading={ordersLoading}
                extra={
                  <Space>
                    <Text type="secondary">
                      Selected {selectedOrders.length}/{selectableOrderIds.length}
                    </Text>
                    <Button type="primary" disabled={!selectedOrders.length} onClick={handleCreateInvoice}>
                      Create invoice
                    </Button>
                  </Space>
                }
              >
                {/* Match Sales Report table header UX */}
                <Card className="mb-4" size="small">
                  <div className="flex flex-wrap items-center justify-between">
                    <div className="flex items-center gap-6">
                      <Radio.Group
                        value={ordersViewMode}
                        onChange={(e) => setOrdersViewMode(e.target.value)}
                        optionType="button"
                        buttonStyle="solid"
                      >
                        <Radio.Button value="default">Default View</Radio.Button>
                        <Radio.Button value="items">Show Order Items</Radio.Button>
                      </Radio.Group>
                      <Tooltip title="Default view shows orders in a flat list. 'Show Order Items' expands each order to show products inside the order.">
                        <InfoCircleOutlined className="text-gray-400" />
                      </Tooltip>
                    </div>
                  </div>
                </Card>

                <Table
                  rowKey="id"
                  dataSource={orders}
                  columns={orderColumns}
                  size="small"
                  className="rounded-lg shadow-sm"
                  pagination={getInvoiceBuilderPagination({
                    showAll: showAllOrders,
                    total: orders.length,
                    pageSize: ordersPageSize,
                    current: ordersPage,
                    onChange: (page, size) => {
                      setOrdersPage(size === ordersPageSize ? page : 1);
                      setOrdersPageSize(size);
                    },
                    onToggle: () => {
                      setOrdersPage(1);
                      setShowAllOrders(value => !value);
                    },
                  })}
                  rowSelection={{
                    preserveSelectedRowKeys: true,
                    selectedRowKeys: selectedOrders,
                    onChange: (keys, _rows, info) => {
                      // AntD header checkbox only selects current page by default.
                      // Override so "select all" selects ALL invoiceable orders across pages.
                      if (info?.type === 'all') return;
                      setSelectedOrders(keys);
                    },
                    onSelectAll: (selected) => {
                      setSelectedOrders(selected ? selectableOrderIds : []);
                    },
                    getCheckboxProps: (row) => ({ disabled: !!row.invoice_id }),
                    selections: [
                      {
                        key: 'select-all-orders',
                        text: 'Select all orders',
                        onSelect: () => setSelectedOrders(selectableOrderIds),
                      },
                      {
                        key: 'clear-orders',
                        text: 'Clear selection',
                        onSelect: () => setSelectedOrders([]),
                      },
                    ],
                  }}
                  expandable={
                    ordersViewMode === 'items'
                      ? {
                          expandedRowRender: expandedOrderItems,
                          expandIcon: ({ expanded, onExpand, record }) =>
                            expanded ? (
                              <DownOutlined className="text-blue-500 mr-2" onClick={(e) => onExpand(record, e)} />
                            ) : (
                              <RightOutlined className="text-gray-400 mr-2" onClick={(e) => onExpand(record, e)} />
                            ),
                        }
                      : undefined
                  }
                  scroll={{ x: 1200 }}
                />
              </Card>
            ),
          },
          {
            key: 'invoices',
            label: 'Invoices',
            children: (
              <Card title="Invoices" loading={invoiceLoading}>
                <Table
                  rowKey="id"
                  dataSource={invoices}
                  pagination={getInvoiceBuilderPagination({
                    showAll: showAllInvoices,
                    total: invoices.length,
                    pageSize: invoicesPageSize,
                    current: invoicesPage,
                    onChange: (page, size) => {
                      setInvoicesPage(size === invoicesPageSize ? page : 1);
                      setInvoicesPageSize(size);
                    },
                    onToggle: () => {
                      setInvoicesPage(1);
                      setShowAllInvoices(value => !value);
                    },
                  })}
                  scroll={{ x: 1450 }}
                  size="small"
                  columns={[
                    { title: 'Invoice #', dataIndex: 'number', width: 160, render: (v, row) => v || `#${row.id}` },
                    { title: 'Client', dataIndex: 'customer_name', width: 160, ellipsis: true },
                    { title: 'Reference', dataIndex: 'reference', width: 140, ellipsis: true, render: (v) => v || '—' },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      width: 105,
                      render: (status) => {
                        const color = status === 'paid' ? 'green' : status === 'cancelled' ? 'red' : 'blue';
                        return <Tag color={color}>{status || 'draft'}</Tag>;
                      },
                    },
                    {
                      title: 'Xero',
                      key: 'xero',
                      width: 110,
                      render: (_, row) => {
                        if (row.xero_invoice_id) return <Tag color="geekblue">Synced</Tag>;
                        if (row.xero_last_error) return <Tooltip title={row.xero_last_error}><Tag color="red">Error</Tag></Tooltip>;
                        return <Tag>Not synced</Tag>;
                      },
                    },
                    {
                      title: 'Xero Invoice #',
                      dataIndex: 'xero_invoice_number',
                      width: 105,
                      ellipsis: true,
                      render: (value) => value || '—',
                    },
                    {
                      title: 'Xero Account',
                      dataIndex: 'xero_account_name',
                      width: 130,
                      ellipsis: true,
                      render: (value) => value || 'Legacy account',
                    },
                    { title: 'Total', dataIndex: 'total', width: 115, align: 'right', render: formatCurrency },
                    {
                      title: 'Issued',
                      dataIndex: 'created_at',
                      width: 115,
                      render: (value) => (value ? moment(value).format('YYYY-MM-DD') : '—'),
                    },
                    {
                      title: 'Actions',
                      width: 310,
                      fixed: 'right',
                      render: (_, row) => (
                        <Space size="small">
                          <Tooltip title="Preview">
                            <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => openPreviewModal(row)} />
                          </Tooltip>
                          <Dropdown
                            trigger={['click']}
                            menu={{
                              items: [
                                { key: 'pdf-with-items', label: 'PDF with items', onClick: () => handlePdf(row.id, { includeItems: true }) },
                                { key: 'pdf-without-items', label: 'PDF without items', onClick: () => handlePdf(row.id, { includeItems: false }) },
                              ],
                            }}
                          >
                            <Tooltip title="PDF">
                              <Button size="small" type="text" icon={<FilePdfOutlined />} />
                            </Tooltip>
                          </Dropdown>
                          <Tooltip title="Print">
                            <Button size="small" type="text" icon={<PrinterOutlined />} onClick={() => handlePrint(row.id)} />
                          </Tooltip>
                          <Tooltip title="Edit">
                            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEditModal(row)} />
                          </Tooltip>
                          <Tooltip
                            title={
                              !clientId
                                ? 'Select a client to enable Xero sync'
                                : !canSyncToXero(row)
                                  ? 'Xero account not connected'
                                  : 'Sync to Xero'
                            }
                          >
                            <Button
                              size="small"
                              type="text"
                              icon={<ReloadOutlined />}
                              onClick={() => handleSyncToXero(row)}
                              disabled={!canSyncToXero(row) || xeroLoading || xeroAccountsLoading}
                            />
                          </Tooltip>
                          {row.xero_invoice_id && (
                            <Tooltip title="View in Xero">
                              <Button
                                size="small"
                                type="text"
                                icon={<FileSearchOutlined />}
                                onClick={() => {
                                  const url = getXeroViewUrl(row.xero_invoice_id);
                                  if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                }}
                              />
                            </Tooltip>
                          )}
                          {row.status === 'paid' ? (
                            <Tooltip title="Revert paid (back to draft)">
                              <Button
                                size="small"
                                type="text"
                                icon={<RollbackOutlined />}
                                onClick={() => handleRevertPaid(row.id)}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Mark paid">
                              <Button size="small" type="text" icon={<CheckCircleOutlined />} onClick={() => handleMarkPaid(row.id)} />
                            </Tooltip>
                          )}
                          {row.status !== 'paid' && (
                            <Tooltip title={row.can_capture_saved_card
                              ? `Capture payment from ${row.stripe_account_label || 'saved card'}`
                              : 'Capture payment becomes available when this client has a saved card'}>
                              <Button
                                size="small"
                                type="text"
                                icon={<CreditCardOutlined />}
                                disabled={!row.can_capture_saved_card}
                                loading={capturingInvoiceId === row.id}
                                onClick={() => handleCaptureInvoice(row)}
                              />
                            </Tooltip>
                          )}
                          <Tooltip title="Delete">
                            <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteInvoice(row.id)} />
                          </Tooltip>
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />

      {/* SETTINGS MODAL */}
      <Modal
        open={settingsOpen}
        onCancel={() => setSettingsOpen(false)}
        title="Invoice settings"
        width={920}
        okText="Save"
        onOk={handleSaveSettings}
        confirmLoading={settingsSaving}
      >
        <Text type="secondary">
          These settings control the invoice PDF appearance and numbering. Defaults are pre-filled.
        </Text>
        <Divider />
        <Form layout="vertical" form={settingsForm} initialValues={settings}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Organization name" name="organization_name">
                <Input placeholder={DEFAULT_SETTINGS.organization_name} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Organization phone" name="organization_phone">
                <Input placeholder={DEFAULT_SETTINGS.organization_phone} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Organization address" name="organization_address">
                <Input placeholder="123 Street, City" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="VAT rate (%)" name="vat_rate">
                <InputNumber style={{ width: '100%' }} min={0} step={0.01} placeholder="20" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Primary color" name="primary_color">
                <Input placeholder={DEFAULT_SETTINGS.primary_color} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Accent color" name="accent_color">
                <Input placeholder={DEFAULT_SETTINGS.accent_color} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Footer text" name="footer_text">
                <Input.TextArea rows={3} placeholder={DEFAULT_SETTINGS.footer_text} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Invoice number prefix" name="invoice_number_prefix">
                <Input placeholder={DEFAULT_SETTINGS.invoice_number_prefix} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Invoice number padding" name="invoice_number_padding">
                <InputNumber style={{ width: '100%' }} min={0} max={20} step={1} placeholder="5" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Next invoice number" name="invoice_number_next">
                <InputNumber style={{ width: '100%' }} min={1} step={1} placeholder="1" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Invoice number format"
                name="invoice_number_format"
                extra="Placeholders: {PREFIX}, {SEQ}, {SEQ_RAW}, {YYYY}, {YY}, {MM}, {DD}. Example: {PREFIX}{YYYY}-{SEQ}"
              >
                <Input placeholder={DEFAULT_SETTINGS.invoice_number_format} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Invoice logo">
                <Upload
                  beforeUpload={(file) => {
                    setLogoFile(file);
                    return false;
                  }}
                  onRemove={() => setLogoFile(undefined)}
                  maxCount={1}
                  fileList={
                    logoFile
                      ? [{ uid: 'logo', name: logoFile.name }]
                      : settings?.logo_url
                        ? [{ uid: 'existing', name: 'Current logo', url: settings.logo_url }]
                        : []
                  }
                  listType="picture"
                  showUploadList={{ showRemoveIcon: true }}
                >
                  <Button>Select logo</Button>
                </Upload>
                {settings?.logo_url && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">Current: </Text>
                    <img src={settings.logo_url} alt="Logo" style={{ maxHeight: 50 }} />
                  </div>
                )}
              </Form.Item>
            </Col>
            <Col span={24}>
              <Space>
                <Button onClick={() => { loadSettings(); message.info('Defaults restored from server'); }}>
                  Reset
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* INVOICE EDIT MODAL */}
      <Modal
        open={editModal.open}
        onCancel={() => setEditModal({ open: false, invoice: null })}
        onOk={handleUpdateInvoice}
        title={editModal.invoice ? `Edit invoice ${editModal.invoice.number || editModal.invoice.id}` : 'Edit invoice'}
        width={980}
        okText="Save changes"
      >
        <div className="mb-3">
          <Text type="secondary">
            Select orders for this invoice. Add one optional custom line (discount/surcharge) which affects the invoice total.
          </Text>
        </div>

        <Row gutter={12} className="mb-3">
          <Col span={24}>
            <Text strong>Reference</Text>
            <div className="text-sm text-gray-500">This is sent to Xero as the invoice “Reference”.</div>
            <Input
              placeholder="Reference"
              value={editReference}
              onChange={(e) => setEditReference(e.target.value)}
            />
          </Col>
        </Row>

        <Card className="mb-4" size="small">
          <div className="flex flex-wrap items-center justify-between">
            <div className="flex items-center gap-6">
              <Radio.Group
                value={ordersViewMode}
                onChange={(e) => setOrdersViewMode(e.target.value)}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value="default">Default View</Radio.Button>
                <Radio.Button value="items">Show Order Items</Radio.Button>
              </Radio.Group>
              <Tooltip title="Default view shows orders in a flat list. 'Show Order Items' expands each order to show products inside the order.">
                <InfoCircleOutlined className="text-gray-400" />
              </Tooltip>
            </div>
          </div>
        </Card>

        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <Text type="secondary">
            Selected {editSelection.length}/{editSelectableOrderIds.length}
          </Text>
          <Space>
            <Button
              size="small"
              onClick={() => setEditSelection(editSelectableOrderIds)}
              disabled={!editSelectableOrderIds.length}
            >
              Select all orders
            </Button>
            <Button
              size="small"
              onClick={() => setEditSelection([])}
              disabled={!editSelection.length}
            >
              Clear selection
            </Button>
          </Space>
        </div>

        <Table
          rowKey="id"
          dataSource={editOrders}
          columns={orderColumns.filter((c) => c.dataIndex !== 'invoice_id' && c.title !== 'Invoice')}
          size="middle"
          className="rounded-lg shadow-sm"
          pagination={false}
          scroll={{ y: 450, x: 900 }}
          rowSelection={{
            selectedRowKeys: editSelection,
            onChange: (keys) => setEditSelection(keys),
            getCheckboxProps: (row) => ({
              disabled: !!row.invoice_id && row.invoice_id !== editInvoiceId,
            }),
            selections: [
              {
                key: 'select-all-edit-orders',
                text: 'Select all orders',
                onSelect: () => setEditSelection(editSelectableOrderIds),
              },
              {
                key: 'clear-edit-orders',
                text: 'Clear selection',
                onSelect: () => setEditSelection([]),
              },
            ],
          }}
          expandable={
            ordersViewMode === 'items'
              ? {
                  expandedRowRender: expandedOrderItems,
                  expandIcon: ({ expanded, onExpand, record }) =>
                    expanded ? (
                      <DownOutlined className="text-blue-500 mr-2" onClick={(e) => onExpand(record, e)} />
                    ) : (
                      <RightOutlined className="text-gray-400 mr-2" onClick={(e) => onExpand(record, e)} />
                    ),
                }
              : undefined
          }
        />

        <Divider />
        <Row gutter={12} align="middle">
          <Col span={24}>
            <Text strong>Extra line (optional)</Text>
            <div className="text-sm text-gray-500">
              Use a negative unit price for discounts (e.g. “Discount”, qty 1, unit price -120).
              If the invoice is already in Xero, Save updates that same Xero InvoiceID; it does not create a duplicate.
            </div>
          </Col>
          <Col xs={24} md={12}>
            <Input
              placeholder="Description (e.g. Discount / Surcharge)"
              value={extraLine.description}
              onChange={(e) => setExtraLine((s) => ({ ...s, description: e.target.value }))}
            />
          </Col>
          <Col xs={24} md={6}>
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Qty"
              min={0}
              step={0.01}
              value={extraLine.qty}
              onChange={(v) => setExtraLine((s) => ({ ...s, qty: v }))}
            />
          </Col>
          <Col xs={24} md={6}>
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Unit price"
              step={0.01}
              value={extraLine.unit_price}
              onChange={(v) => setExtraLine((s) => ({ ...s, unit_price: v }))}
              formatter={(v) => (v === undefined || v === null ? '' : `£ ${v}`)}
              parser={(v) => (v ? v.replace(/[£\s]/g, '') : '')}
            />
          </Col>
          <Col span={24}>
            <Text type="secondary">
              Extra line total:{' '}
              <Text strong>
                {formatCurrency(
                  extraLine.description?.trim() && extraLine.qty != null && extraLine.unit_price != null
                    ? Number(extraLine.qty) * Number(extraLine.unit_price)
                    : 0
                )}
              </Text>
            </Text>
          </Col>
        </Row>
      </Modal>

      {/* PREVIEW MODAL */}
      <Modal
        open={previewModal.open}
        onCancel={() => {
          setPreviewModal({ open: false, invoice: null });
          setPreviewMode('list');
          setPreviewOrdersView('table');
          setPreviewTableMode('default');
          setPdfVariant('withItems');
          if (pdfUrls) {
            Object.values(pdfUrls).forEach((url) => {
              try {
                window.URL.revokeObjectURL(url);
              } catch (e) {
                // ignore
              }
            });
            setPdfUrls({});
          }
        }}
        footer={null}
        width={1100}
        title={
          previewModal.invoice
            ? `Invoice ${previewModal.invoice.number || `#${previewModal.invoice.id}`}`
            : 'Invoice preview'
        }
      >
        {previewModal.invoice && (
          <>
            <Row gutter={12} align="middle" className="mb-3">
              <Col flex="auto">
                <Space direction="vertical" size={0}>
                  <Text type="secondary">Client</Text>
                  <Text strong>{previewModal.invoice.customer_name || '—'}</Text>
                </Space>
              </Col>
              <Col>
                <Space direction="vertical" size={0}>
                  <Text type="secondary">Reference</Text>
                  <Text strong>{previewModal.invoice.reference || '—'}</Text>
                </Space>
              </Col>
              <Col>
                <Space direction="vertical" size={0}>
                  <Text type="secondary">Issued</Text>
                  <Text strong>
                    {previewModal.invoice.created_at ? moment(previewModal.invoice.created_at).format('YYYY-MM-DD') : '—'}
                  </Text>
                </Space>
              </Col>
              <Col>
                <Space direction="vertical" size={0}>
                  <Text type="secondary">Status</Text>
                  <Tag color={previewModal.invoice.status === 'paid' ? 'green' : 'blue'}>
                    {previewModal.invoice.status || 'draft'}
                  </Tag>
                </Space>
              </Col>
              <Col>
                <Segmented
                  value={previewMode}
                  onChange={(val) => {
                    setPreviewMode(val);
                    if (val === 'pdf') ensurePdfLoaded(previewModal.invoice.id, pdfVariant);
                  }}
                  options={[
                    { label: 'List', value: 'list' },
                    { label: 'PDF', value: 'pdf' },
                  ]}
                />
              </Col>
              {previewMode === 'pdf' && (
                <Col>
                  <Space direction="vertical" size={0}>
                    <Text type="secondary">PDF view</Text>
                    <Segmented
                      size="small"
                      value={pdfVariant}
                      onChange={(val) => {
                        setPdfVariant(val);
                        ensurePdfLoaded(previewModal.invoice.id, val);
                      }}
                      options={[
                        { label: 'With items', value: 'withItems' },
                        { label: 'Summary only', value: 'summary' },
                      ]}
                    />
                  </Space>
                </Col>
              )}
              <Col>
                <Space>
                  <Button
                    icon={<FilePdfOutlined />}
                    onClick={() =>
                      handlePdf(previewModal.invoice.id, { includeItems: pdfVariant === 'withItems' })
                    }
                  >
                    Open PDF
                  </Button>
                  <Button
                    icon={<PrinterOutlined />}
                    onClick={() =>
                      handlePrint(previewModal.invoice.id, { includeItems: pdfVariant === 'withItems' })
                    }
                  >
                    Print
                  </Button>
                </Space>
              </Col>
            </Row>

            {previewMode === 'list' ? (
              <>
                <Row gutter={[12, 12]} className="mb-3">
                  <Col xs={24} md={8}>
                    <Card size="small" className="shadow-sm">
                      <Text type="secondary">Invoice subtotal</Text>
                      <Title level={4} style={{ margin: '4px 0' }}>
                        {formatCurrency(previewSubtotal)}
                      </Title>
                      <Text type="secondary">{previewOrdersRows.length} orders</Text>
                    </Card>
                  </Col>
                  <Col xs={24} md={8}>
                    <Card size="small" className="shadow-sm">
                      <Text type="secondary">{previewModal.invoice.extra_line_description || 'Extra line'}</Text>
                      <Title level={4} style={{ margin: '4px 0' }}>
                        {formatCurrency(previewExtraTotal)}
                      </Title>
                      <Text type="secondary">
                        {previewModal.invoice.extra_line_description ? 'Applied once' : 'No adjustment'}
                      </Text>
                    </Card>
                  </Col>
                  <Col xs={24} md={8}>
                    <Card size="small" className="shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <Text type="secondary">Invoice total</Text>
                          <Title level={4} style={{ margin: '4px 0' }}>
                            {formatCurrency(previewSubtotal + previewExtraTotal)}
                          </Title>
                          <Text type="secondary">Balance overview</Text>
                        </div>
                        <Tag color={previewModal.invoice.status === 'paid' ? 'green' : 'blue'}>
                          {previewModal.invoice.status || 'draft'}
                        </Tag>
                      </div>
                    </Card>
                  </Col>
                </Row>

                <Card size="small" className="mb-3 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <Space>
                      <Text type="secondary">Orders view</Text>
                      <Segmented
                        value={previewOrdersView}
                        onChange={setPreviewOrdersView}
                        options={[
                          { label: 'Table view', value: 'table' },
                          { label: 'Orders + items', value: 'detailed' },
                        ]}
                      />
                    </Space>
                    {previewOrdersView === 'table' && (
                      <Space>
                        <Text type="secondary">Table mode</Text>
                        <Radio.Group
                          value={previewTableMode}
                          onChange={(e) => setPreviewTableMode(e.target.value)}
                          optionType="button"
                          buttonStyle="solid"
                          size="small"
                        >
                          <Radio.Button value="default">Default</Radio.Button>
                          <Radio.Button value="items">Items below</Radio.Button>
                        </Radio.Group>
                        <Tooltip title="Default shows a compact order list. 'Items below' expands orders so you can see product lines without leaving the preview.">
                          <InfoCircleOutlined className="text-gray-400" />
                        </Tooltip>
                      </Space>
                    )}
                  </div>
                </Card>

                {previewOrdersView === 'table' ? (
                  <Table
                    rowKey="id"
                    dataSource={previewOrdersRows}
                    columns={previewColumns}
                    pagination={false}
                    size="middle"
                    scroll={{ y: 450, x: 900 }}
                    expandable={
                      previewTableMode === 'items'
                        ? {
                            expandedRowRender: expandedOrderItems,
                            expandIcon: ({ expanded, onExpand, record }) =>
                              expanded ? (
                                <DownOutlined className="text-blue-500 mr-2" onClick={(e) => onExpand(record, e)} />
                              ) : (
                                <RightOutlined className="text-gray-400 mr-2" onClick={(e) => onExpand(record, e)} />
                              ),
                          }
                        : undefined
                    }
                    summary={() => {
                      const summaryColSpan = Math.max(1, previewColumns.length - 1);
                      return (
                        <>
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={summaryColSpan} className="text-right">
                              <Text type="secondary">Subtotal:</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} className="text-right">
                              <Text>{formatCurrency(previewSubtotal)}</Text>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                          {previewExtraTotal !== 0 && (
                            <Table.Summary.Row>
                              <Table.Summary.Cell index={0} colSpan={summaryColSpan} className="text-right">
                                <Text type="secondary">
                                  {previewModal.invoice.extra_line_description}:
                                </Text>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={1} className="text-right">
                                <Text>{formatCurrency(previewExtraTotal)}</Text>
                              </Table.Summary.Cell>
                            </Table.Summary.Row>
                          )}
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={summaryColSpan} className="text-right">
                              <Text strong>Total:</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} className="text-right">
                              <Text strong>{formatCurrency(previewSubtotal + previewExtraTotal)}</Text>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        </>
                      );
                    }}
                  />
                ) : (
                  renderPreviewOrderCards()
                )}
              </>
            ) : (
              <div style={{ height: 520 }}>
                {pdfLoading && <Text type="secondary">Loading PDF…</Text>}
                {pdfUrls[pdfVariant] && (
                  <iframe
                    ref={pdfIframeRef}
                    title="invoice-pdf"
                    src={pdfUrls[pdfVariant]}
                    style={{ width: '100%', height: '100%', border: '1px solid #e5e7eb', borderRadius: 8 }}
                  />
                )}
                {!pdfLoading && !pdfUrls[pdfVariant] && (
                  <div className="text-center text-gray-500" style={{ paddingTop: 80 }}>
                    <Text type="secondary">PDF preview not loaded yet.</Text>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Modal>

      {/* CREATE INVOICE REFERENCE MODAL */}
      <Modal
        open={createReferenceModalOpen}
        onCancel={() => setCreateReferenceModalOpen(false)}
        onOk={submitCreateInvoice}
        confirmLoading={createInvoiceSubmitting}
        title="Invoice reference"
        okText="Create invoice"
      >
        <Text type="secondary">
          Enter a reference for this invoice. This will be sent to Xero as the invoice “Reference”.
        </Text>
        <Divider />
        <Input
          autoFocus
          placeholder="Reference"
          value={createReference}
          onChange={(e) => setCreateReference(e.target.value)}
          onPressEnter={submitCreateInvoice}
        />
        {(xeroAccountsLoading || xeroAccounts.some(account => account.status === 'connected' && account.is_active)) && (
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Xero account</Text>
            <Select
              loading={xeroAccountsLoading}
              value={createXeroAccountId}
              onChange={setCreateXeroAccountId}
              placeholder="Choose which Xero account will receive this invoice"
              style={{ width: '100%', marginTop: 6 }}
              options={xeroAccounts
                .filter(account => account.status === 'connected' && account.is_active)
                .map(account => ({
                label: `${account.display_name || account.xero_tenant_name}${account.oauth_app === 'xero2' ? ' · Linen' : ' · Golden'}${account.is_default ? ' (Default)' : ''}`,
                value: account.id,
              }))}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                Accounts are managed in <a href="/admin/integrations/xero">Integrations → Xero Accounts</a>.
              </Text>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
